import { useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';

import AppsIcon from '@mui/icons-material/Apps';

import CheckIcon from '@mui/icons-material/Check';

import PointOfSaleIcon from '@mui/icons-material/PointOfSale';

import HubIcon from '@mui/icons-material/Hub';

import GroupsIcon from '@mui/icons-material/Groups';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import AssignmentIcon from '@mui/icons-material/Assignment';

import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import ShareIcon from '@mui/icons-material/Share';

import RequestQuoteIcon from '@mui/icons-material/RequestQuote';

import LocalShippingIcon from '@mui/icons-material/LocalShipping';

import CampaignIcon from '@mui/icons-material/Campaign';

import type { SvgIconComponent } from '@mui/icons-material';



import { getModuleEntryByErpModule } from '@/config/moduleEntries';

import type { ErpModule } from '@/api/platform.api';

import { useAppDispatch } from '@/hooks/useAppDispatch';

import { useAppSelector } from '@/hooks/useAppSelector';

import { switchWorkspace } from '@/store/authSlice';
import { usePermissions } from '@/hooks/usePermissions';

import { getLauncherModules } from '@/utils/moduleAccess';
import { rememberPreSwitchPath } from '@/utils/moduleNavigation';
import { isHeadModuleSubscribed } from '@/hooks/useLauncherModuleSync';
import { notify } from '@/core/toast/notify';
import { cn } from '@/utils/cn';



const MODULE_ICONS: Partial<Record<ErpModule, SvgIconComponent>> = {

  POS: PointOfSaleIcon,

  CRM: HubIcon,

  HR: GroupsIcon,

  FINANCE: AccountBalanceIcon,

  PROJECTS: AssignmentIcon,

  DESK: SupportAgentIcon,

  SOCIAL: ShareIcon,

  PROCUREMENT: RequestQuoteIcon,

  WAREHOUSE: LocalShippingIcon,

  LEAD_GEN: CampaignIcon,

  MARKETING: CampaignIcon,

};



const TILE_ICON_CLASS =

  'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';



interface AppLauncherProps {

  activeModule: ErpModule;

}



export function AppLauncher({ activeModule }: AppLauncherProps) {

  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const location = useLocation();

  const enabledModules = useAppSelector((s) => s.auth.enabledModules);

  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);

  const permissions = useAppSelector((s) => s.auth.user?.permissions ?? []);
  const roleCode = useAppSelector((s) => s.auth.user?.roleCode);
  const switching = useAppSelector((s) => s.auth.loading);
  const { has } = usePermissions();

  const [open, setOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);

  const subscribedApps = useMemo(
    () => getLauncherModules(enabledModules, permissions, accessibleModules, roleCode, has),
    [enabledModules, permissions, accessibleModules, roleCode, has],
  );

  const canSwitchApps = subscribedApps.length >= 2;



  useEffect(() => {

    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {

      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {

        setOpen(false);

      }

    };

    const onEscape = (event: KeyboardEvent) => {

      if (event.key === 'Escape') setOpen(false);

    };

    document.addEventListener('mousedown', onPointerDown);

    document.addEventListener('keydown', onEscape);

    return () => {

      document.removeEventListener('mousedown', onPointerDown);

      document.removeEventListener('keydown', onEscape);

    };

  }, [open]);



  if (!canSwitchApps) {

    return null;

  }



  const handleSwitch = async (module: ErpModule) => {
    if (module === activeModule) {
      setOpen(false);
      return;
    }

    if (!isHeadModuleSubscribed(module, enabledModules, accessibleModules)) {
      notify.error(`Module ${module} is not enabled for this organization`);
      setOpen(false);
      return;
    }

    if (!subscribedApps.some((entry) => entry.erpModule === module)) {
      notify.error('You do not have access to this application');
      setOpen(false);
      return;
    }

    rememberPreSwitchPath(location.pathname, location.search);
    setOpen(false);

    try {
      await dispatch(switchWorkspace(module)).unwrap();
      const home = getModuleEntryByErpModule(module)?.homePath ?? '/org';
      navigate(home);
      notify.success(`Switched to ${getModuleEntryByErpModule(module)?.label ?? module}`);
    } catch {
      notify.error('Could not switch workspace');
    }
  };



  return (

    <div ref={rootRef} className="relative">

      <button

        type="button"

        onClick={() => setOpen((v) => !v)}

        className={cn(
          'app-launcher-trigger',
          open && 'app-launcher-trigger--open',
          switching && 'pointer-events-none opacity-60',
        )}
        disabled={switching}
        title="Switch application"

        aria-expanded={open}

        aria-haspopup="true"

      >

        <AppsIcon fontSize="small" />

        <span className="hidden sm:inline">Apps</span>

        <span className="hidden text-slate-400 sm:inline" aria-hidden>

          ▼

        </span>

      </button>



      {open && (

        <div className="app-launcher-panel" role="menu">

          <div className="app-launcher-panel-header">

            <p className="app-launcher-panel-title">Your products</p>

            <p className="app-launcher-panel-subtitle">Only apps enabled for your organization</p>

          </div>

          <div className="app-launcher-grid">

            {subscribedApps.map((entry) => {

              const Icon = MODULE_ICONS[entry.erpModule] ?? HubIcon;

              const isActive = entry.erpModule === activeModule;

              return (

                <button

                  key={entry.slug}

                  type="button"

                  role="menuitem"

                  onClick={() => handleSwitch(entry.erpModule)}

                  className={cn('app-launcher-tile', isActive && 'app-launcher-tile--active')}

                >

                  <span className={cn('app-launcher-tile-icon', TILE_ICON_CLASS)}>

                    <Icon fontSize="small" />

                  </span>

                  <span className="app-launcher-tile-label">{entry.label}</span>

                  {isActive && <CheckIcon className="app-launcher-tile-check" sx={{ fontSize: 16 }} />}

                </button>

              );

            })}

          </div>

        </div>

      )}

    </div>

  );

}


